import { resetPluCallback, setPluCallback } from 'src/bottom-area/business-logic/handle-plu-items';
import { EmeraldError, EmeraldErrorModal } from 'src/common/api/emerald-error';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import { UnitOfMeasureTypes } from 'src/common/enumerations/unit-of-measure-type';
import { removeSpecialCharacters } from 'src/common/helpers/character-escaper';
import { ItemNotFound } from 'src/common/helpers/error-code';
import { LocalStorage } from 'src/common/helpers/local-storage';
import { getValueAsString } from 'src/common/helpers/string-helper';
import { IDataInformation } from 'src/common/interfaces/dtos/IDataInformation';
import { IItemHierarchyLookup } from 'src/common/interfaces/dtos/IItemHierarchyLookup';
import { IItemHierarchyLookupDto } from 'src/common/interfaces/dtos/IItemHierarchyLookupDto';
import { IItemLookup, IMerchandiseHierarchy } from 'src/common/interfaces/dtos/IItemLookup';
import { IPromotionLookup } from 'src/common/interfaces/dtos/IPromotionLookup';
import { IPromotionsLookupDto } from 'src/common/interfaces/dtos/IPromotionsLookupDto';
import { ISelectedItem } from 'src/common/interfaces/dtos/ISelectedItem';
import { IItemInputData } from 'src/common/interfaces/IItemInputData';
import { PriceCheckActions } from 'src/common/redux/reducers/price-check-response-reducer';
import { isValidQuantity } from 'src/home-page/business-logic/common/item-quantity-validator';
import {
  addLookedupItemToTransaction,
  isLetterOrDigit,
} from 'src/home-page/business-logic/sale-mode-router';
import { dataAdd } from 'src/home-page/business-logic/service/data-add';
import { voidEmptyTransaction } from 'src/home-page/business-logic/void-transaction';
import { initiateTransaction } from 'src/items-list/business-logic/initiate-transaction';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { resetSalesKeypadState } from 'src/sales-keypad/business-logic/sales-keypad-operations';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { restorePage } from '../../common/components/emerald-home-page/emerald-home-page';
import { store } from '../../common/redux/configure-store';
import { IPriceCheckProps, showPriceCheckContainer } from '../component/price-check';
import { itemHierarchyLookup } from './service/item-hierarchy-lookup';
import { priceQuery } from './service/price-check';
import { promotionsLookup } from './service/promotions-lookup';

export const handlePriceCheck = async (): Promise<void> => {
  const closePriceCheckWindow = (): void => {
    restorePage();
    resetPluCallback();
    store.dispatch(PriceCheckActions.resetState());
  };

  const onKeypadSubmit = async (itemCode: string): Promise<void> => {
    await queryItemPrice(itemCode, EntryMethod.Keyed).catch(popupMessage);
  };

  const onScan = async (itemCode: string): Promise<void> => {
    return queryItemPrice(itemCode, EntryMethod.Scanned)
      .catch(popupMessage)
      .finally(resetSalesKeypadState);
  };

  const pluCallback = async (selectedItem?: ISelectedItem, itemId?: string): Promise<void> => {
    itemId = itemId ?? selectedItem?.selectedItemId ?? '';
    return queryItemPrice(itemId, EntryMethod.Tapped, selectedItem)
      .catch(popupMessage)
      .finally(resetSalesKeypadState);
  };

  const onSell = async (
    itemlookup: IItemLookup,
    quantity: number,
    isManualWeight?: boolean,
    entryMethod?: EntryMethod,
    itemCode?: string,
  ) => {
    if (itemlookup.isBarcodeItem && itemlookup.packQuantity?.value && quantity > 1) {
      popupMessage(
        new EmeraldErrorModal({
          type: EmeraldDialogTypes.Error,
          message: 'QuantityWithBarcodeForbidden',
          disableBackdropClick: true,
          acceptButtonLabel: 'Close',
          hideCancelButton: true,
        }),
      );
    } else {
      const isWeightEmbeddedInBarcode =
        itemlookup.unitsOfMeasure !== UnitOfMeasureTypes.Each &&
        itemlookup.packQuantity?.units !== undefined;
      const inputData: IItemInputData = {
        itemId: itemCode,
        quantity: itemlookup.packQuantity?.value ? itemlookup.packQuantity.value : quantity,
        quantityEntryMethod: EntryMethod.Automatic,
        isManualWeight,
        entryMethod: entryMethod,
      };

      return addLookedupItemToTransaction(inputData, itemlookup, isWeightEmbeddedInBarcode)
        .then(closePriceCheckWindow)
        .catch(async err => {
          store.dispatch(PriceCheckActions.resetState());
          popupMessage(err);
        })
        .finally(resetSalesKeypadState);
    }
  };

  const onCancel = (): void => {
    closePriceCheckWindow();
    voidEmptyTransaction();
    store.dispatch(PriceCheckActions.resetState());
  };

  const props: IPriceCheckProps = {
    onCancel,
    onKeypadSubmit,
    onSell,
  };

  setPluCallback(pluCallback);
  store.dispatch(PriceCheckActions.resetState());
  showPriceCheckContainer(props, onScan);
};

const getSelectedItem = (selectedItem: ISelectedItem): IItemLookup => {
  return {
    description: selectedItem.name,
    unitsOfMeasure: selectedItem.unitOfMeasure,
    itemId: selectedItem.selectedItemId,
    regularSalesUnitPrice: {
      price: { value: selectedItem.price },
      quantity: selectedItem.quantity,
    },
    hasPrice: !!selectedItem.price,
    unitPrice: selectedItem.price,
  };
};

export const queryItemPrice = async (
  itemId: string,
  entryMethod?: EntryMethod,
  selectedItem?: ISelectedItem,
): Promise<void> => {
  const { priceCheckDecode } = getCurrentXLRConfig();
  let { itemLookup, promotions, hierarchy } = store.getState().priceCheck;
  const { isManualWeight, metricValue } = store.getState().salesKeypad;
  let itemCode = itemId;
  if (priceCheckDecode) {
    const itemData: IDataInformation = await dataAdd(undefined, {
      itemId: itemId,
      entryMethod: entryMethod,
    }).catch(() => {
      return { itemId: undefined };
    });
    const isEmbeddedBarcode = !!itemData.amount || !!itemData.quantity || !!itemData.price;
    itemCode = (isEmbeddedBarcode ? itemData.scanData : itemData.itemId) ?? itemId;
  }
  await withSpinner(async () => {
    if (!itemCode && itemLookup) {
      if (isValidQuantity(metricValue, itemLookup.unitsOfMeasure, isManualWeight) && itemLookup) {
        store.dispatch(
          PriceCheckActions.setItemState(
            itemLookup,
            metricValue,
            isManualWeight,
            entryMethod,
            itemLookup.itemId,
          ),
        );
      }
    } else if (itemCode && isValid(itemCode)) {
      const { transactionIdExists } = store.getState().transaction;
      if (!transactionIdExists) {
        await initiateTransaction();
      }
      try {
        const { callPriceQueryOnPluItem } = getCurrentXLRConfig();

        if (callPriceQueryOnPluItem || !selectedItem) {
          itemLookup = await priceQuery(itemCode, entryMethod);
        } else {
          itemLookup = getSelectedItem(selectedItem);
        }
        promotions = await getItemPromotions(itemCode);
        if (itemLookup.merchandiseHierarchy) {
          hierarchy = await getItemHierarchy(itemLookup.merchandiseHierarchy);
        }
      } catch (err) {
        await voidEmptyTransaction();

        if (err instanceof EmeraldError && err.code === ItemNotFound) {
          throw new EmeraldErrorModal({
            message: 'ItemErrorControl_ItemNotFound_Begin_With_Id',
            messageArgs: { arg: itemCode },
            type: EmeraldDialogTypes.Warning,
            subMessage: 'ItemErrorControl_ItemNotFound_End',
            acceptButtonLabel: 'Cancel',
          });
        }
        throw err;
      }

      if (itemLookup) {
        if (isValidQuantity(metricValue, itemLookup.unitsOfMeasure, isManualWeight)) {
          store.dispatch(
            PriceCheckActions.setItemState(
              itemLookup,
              metricValue,
              isManualWeight,
              entryMethod,
              itemCode,
            ),
          );
          if (promotions) {
            store.dispatch(PriceCheckActions.setItemPromotions(promotions));
          }
          if (hierarchy) {
            store.dispatch(PriceCheckActions.setItemHierarchy(hierarchy));
          }
        } else {
          store.dispatch(PriceCheckActions.resetState());
        }
      }
    } else {
      throw new EmeraldError('InvalidItemError');
    }
  });
};

export const isValid = (value: number | string): boolean => {
  const escapedInput = removeSpecialCharacters(getValueAsString(value));
  const len = escapedInput.length;
  for (let i = 0; i < len; i++) {
    if (!isLetterOrDigit(escapedInput.charCodeAt(i))) {
      return false;
    }
  }
  return true;
};

export const getItemPromotions = async (itemCode: string): Promise<IPromotionLookup[]> => {
  const lookupDto: IPromotionsLookupDto = {
    itemCode: itemCode,
    retailSegmentIds: [LocalStorage.touchpointGroupId()],
    transactionSequenceNumber: store.getState().transaction.sequenceNumber,
  };
  const data: IPromotionLookup[] = await promotionsLookup(lookupDto);
  return data;
};

export const getItemHierarchy = (
  merchandiseHierarchy: IMerchandiseHierarchy[],
): Promise<IItemHierarchyLookup[]> | undefined => {
  if (merchandiseHierarchy.length > 0) {
    const hierarchyType = 'Merchandise';
    const lookupDto: IItemHierarchyLookupDto = {
      hierarchyType: hierarchyType,
      ids: merchandiseHierarchy
        .filter(m => m.hierarchyType === hierarchyType)
        .map(m => m.nodeId ?? '0'),
    };
    return itemHierarchyLookup(lookupDto);
  }
};
